import { Dispatch, SetStateAction, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";

import { UserContext } from "@/app/providers";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { ProcedureDetails } from "@/features/procedures/model/procedure/ProcedureDetail";
import { Checklist } from "@/features/procedures/model/types/checklist";
import {
	ActiveParentChildNodes,
	ProcedureDetailNode,
} from "@/features/procedures/model/types/node";
import { Procedure } from "@/features/procedures/model/types/procedure";
import { ProcedureGroup } from "@/features/procedures/model/types/procedure";
import { ProcedureFolderNode } from "@/features/procedures/model/types/procedureFolderNode";
import { ProcedureMetaData } from "@/features/procedures/model/types/procedureMetaData";
import { UppdatedProcedureVersion } from "@/features/procedures/model/types/updatedProcedureVersion";
import { useQuery } from "@tanstack/react-query";

import { useFetchProcedureGroups } from "@/service/ProcedureQueries";
import { ProcedureQueryKeys } from "@/service/ProcedureQueries";
import { TaskQueryKeys } from "@/service/TaskQueries";
import { RouteParams, useLocalStorage } from "@/service/Utilities";

export * from "@/service/ProcedureQueries";

export const useFetchActiveProcedure = (id?: number) => {
	const { id: idParam } = useParams<RouteParams>();
	let procedureId = id;

	if (procedureId === undefined) {
		procedureId = parseInt(idParam);
	}

	return useQuery<Procedure, any>({
		queryKey: [ProcedureQueryKeys.procedure, procedureId],
		queryFn: () => SATOSPortal.GetProcedure(procedureId),
		enabled: !!procedureId,
	});
};

export const useFetchActiveProcedureDetails = (idProp?: any) => {
	const { id } = useParams<RouteParams>();

	const procedureId = parseInt(idProp ? idProp : id);

	return useQuery<ProcedureDetails, any>({
		queryKey: [ProcedureQueryKeys.procedureDetails, procedureId],
		queryFn: () => SATOSPortal.GetProcedureDetails(procedureId),
		enabled: !!procedureId,
	});
};

// Helper function to get the first available node or child from procedure nodes
const getFirstAvailableNodeOrChild = (
	nodes: ProcedureDetailNode[],
): { node: ProcedureDetailNode | undefined; isLeaf: boolean } => {
	// First check if there's a leaf node (node without children)
	const firstLeafNode = nodes.find(
		(node) => !node.Children || node.Children.length === 0,
	);
	if (firstLeafNode) {
		return { node: firstLeafNode, isLeaf: true };
	}

	// Otherwise, find the first node with children
	const firstNodeWithChildren = nodes.find(
		(node) => node.Children && node.Children.length > 0,
	);
	if (firstNodeWithChildren?.Children[0]) {
		return { node: firstNodeWithChildren.Children[0], isLeaf: false };
	}

	return { node: undefined, isLeaf: false };
};

const findNodeByTreeNodeNumber = (
	nodes: Array<ProcedureDetailNode>,
	checklistId: number,
): ActiveParentChildNodes => {
	// First check if any node itself has the matching SchemeId (for leaf nodes)
	const leafNode = nodes.find((node) => node.SchemeId === checklistId);
	if (leafNode && (!leafNode.Children || leafNode.Children.length === 0)) {
		// This is a leaf node that matches - return it as the parent with no child
		return { activeParentNode: leafNode, activeChildNode: undefined };
	}

	// Original logic: find parent node that contains the child with this schemeId
	const activeParentNode = nodes.find(
		(node) =>
			node.Children &&
			node.Children.find((child) => child.SchemeId === checklistId),
	);

	const activeChildNode =
		activeParentNode &&
		activeParentNode.Children &&
		activeParentNode.Children.find((child) => child.SchemeId === checklistId);

	return { activeParentNode, activeChildNode };
};

const findActiveNode = (
	procedureDetailNodes: Array<ProcedureDetailNode>,
): ActiveParentChildNodes => {
	// First check for leaf nodes that are active
	const activeLeafNode = procedureDetailNodes.find(
		(node) =>
			(!node.Children || node.Children.length === 0) &&
			(node.CompletedState === "NotStarted" ||
				node.CompletedState === "Started"),
	);

	if (activeLeafNode) {
		// Return the leaf node as parent with no child
		return { activeParentNode: activeLeafNode, activeChildNode: undefined };
	}

	// Original logic: find parent node with active children
	const activeParentNode = procedureDetailNodes.find(
		(node) =>
			node.Children &&
			node.Children.find(
				(child) =>
					child.CompletedState === "NotStarted" ||
					child.CompletedState === "Started",
			),
	);

	const activeChildNode =
		activeParentNode &&
		activeParentNode.Children &&
		activeParentNode.Children.find(
			(child) =>
				child.CompletedState === "NotStarted" ||
				child.CompletedState === "Started",
		);

	return { activeParentNode, activeChildNode };
};

export const useHandleProcedureDetails = (
	nodes: ProcedureDetailNode[] | undefined,
	checklistId?: number,
): ActiveParentChildNodes => {
	// Early return if no nodes
	if (!nodes || nodes.length === 0) {
		return { activeChildNode: undefined, activeParentNode: undefined };
	}
	let activeParentNode: ProcedureDetailNode | undefined;
	let activeChildNode: ProcedureDetailNode | undefined;

	if (checklistId) {
		// Find parent node that contains the child with this schemeId
		const result = findNodeByTreeNodeNumber(nodes, checklistId);
		activeParentNode = result.activeParentNode;
		activeChildNode = result.activeChildNode;
	} else {
		// No schemeId provided, find the first active node based on status
		const result = findActiveNode(nodes);
		activeParentNode = result.activeParentNode;
		activeChildNode = result.activeChildNode;
	}

	// If no active child found, use the first available node or child as fallback
	if (!activeChildNode && !activeParentNode) {
		const fallback = getFirstAvailableNodeOrChild(nodes);
		if (fallback.isLeaf) {
			// For leaf nodes, the node itself is the parent, no child
			activeParentNode = fallback.node;
			activeChildNode = undefined;
		} else {
			// For nodes with children, find the parent of this child
			activeChildNode = fallback.node;
			if (activeChildNode) {
				activeParentNode = nodes.find(
					(node) => node.Children && node.Children.includes(activeChildNode!),
				);
			}
		}
	}

	return { activeChildNode, activeParentNode };
};

export const sortGroupFolders = (
	a: ProcedureGroup,
	b: ProcedureGroup,
): number => {
	if (a.SortNumber === 0 && b.SortNumber === 0) {
		return a.Name.localeCompare(b.Name);
	}

	if (a.SortNumber === 0) {
		return 1;
	}

	if (b.SortNumber === 0) {
		return -1;
	}

	return a.SortNumber - b.SortNumber;
};

export const useFetchActiveProcedureGroupFolders = (): {
	groupList: ProcedureGroup[];
	setGroupList: Dispatch<SetStateAction<ProcedureGroup[]>>;
	closeAllProcedureGroupFolders: () => void;
	openAllProcedureGroupFolders: () => void;
	resetFolderOrderToServer: () => void;
} => {
	const [groupListLocalStorage, setGroupListLocalStorage] = useLocalStorage<
		ProcedureGroup[]
	>("procedureFoldersState", []);

	const { data: groups } = useFetchProcedureGroups(
		ProcedureQueryKeys.orderByNameAsc,
	);

	useEffect(() => {
		if (!groups) return;

		const sortedGroups = [...groups].sort((a, b) => sortGroupFolders(a, b));

		// Initial hydrate – if we have nothing locally yet, seed from server.
		if (!groupListLocalStorage || groupListLocalStorage.length === 0) {
			setGroupListLocalStorage(sortedGroups);
			return;
		}

		// If the number of groups has changed (folder created/deleted), fully resync.
		if (groupListLocalStorage.length !== sortedGroups.length) {
			setGroupListLocalStorage(sortedGroups);
			return;
		}

		// Merge server data (Name, Link, ParentId, etc.) into local while preserving
		// Expanded state. This ensures folder renames/edits from API are reflected in the UI.
		const localById = new Map(groupListLocalStorage.map((g) => [g.Id, g]));
		const merged = sortedGroups.map((serverGroup) => {
			const local = localById.get(serverGroup.Id);
			return {
				...serverGroup,
				Expanded: local?.Expanded ?? false,
			};
		});
		setGroupListLocalStorage(merged);
	}, [groups]);

	const closeAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = false;
				return group;
			}),
		]);
	};

	const openAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = true;
				return group;
			}),
		]);
	};

	return {
		groupList: groupListLocalStorage,
		setGroupList: setGroupListLocalStorage,
		closeAllProcedureGroupFolders,
		openAllProcedureGroupFolders,
		resetFolderOrderToServer: () => {
			if (!groups) return;
			const sorted = [...groups].sort((a, b) => sortGroupFolders(a, b));
			setGroupListLocalStorage(sorted);
		},
	};
};

export const useFetchProcedureUpdateSuggestions = (
	hasMaterialHandling: boolean,
) => {
	return useQuery<any, any>({
		queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
		queryFn: () => SATOSPortal.GetProcedureUpdate(hasMaterialHandling),
	});
};

export const useGetUpdatedProcedures = (companyId: number | undefined) => {
	return useQuery<UppdatedProcedureVersion[], any>({
		queryKey: [ProcedureQueryKeys.procedure, companyId],
		queryFn: () => SATOSPortal.GetUppdatedProcedures(),
		enabled: !!companyId,
	});
};

export const useFetchProcedureNode = (procedureId: number, nodeId: number) => {
	return useQuery<ProcedureDetails, any>({
		queryKey: [ProcedureQueryKeys.procedureNode, procedureId, nodeId],
		queryFn: () => SATOSPortal.GetNode(procedureId, nodeId),
		enabled: !!procedureId && !!nodeId,
		refetchInterval: 10000, // TODO: Remove when proper optimistic update is in place using react-query currentV+1
	});
};

export const useFetchReviewChecklist = (
	procedureIdParam: string | undefined,
	nodeId: number | undefined,
	checklistId: number | undefined,
	isUpdatedChecklist: boolean,
) => {
	const procedureId = procedureIdParam
		? parseInt(procedureIdParam)
		: checklistId;

	return useQuery<Checklist, any>({
		queryKey: [
			ProcedureQueryKeys.updatechecklist,
			procedureId,
			nodeId,
			checklistId,
			isUpdatedChecklist,
		],
		queryFn: () =>
			SATOSPortal.GetReviewChecklist(
				procedureId,
				isUpdatedChecklist,
				nodeId,
				checklistId,
			),
		enabled: !!procedureId && (!!nodeId || !!checklistId),
		refetchInterval: 3000,
	});
};

export const useFetchDocumentForCurrentNode = (request: {
	ProcedureId: number;
	SchemeId: number;
}) => {
	return useQuery({
		queryKey: [
			ProcedureQueryKeys.document,
			ProcedureQueryKeys.node,
			request.SchemeId,
		],
		queryFn: () => SATOSPortal.GetNodeDocument(request),
		enabled: !!request.SchemeId,
	});
};

export const useFetchProcedureMetaData = (filter: string) => {
	const response = useQuery<ProcedureMetaData, any>({
		queryKey: [ProcedureQueryKeys.procedureMetaData],
		queryFn: ({ signal }) => SATOSPortal.GetProcedureMetaData(filter, signal),
	});
	if (response && response.isFetched) {
		if (!response.data?.Folders.find((folder) => folder.Name === "[Root]")) {
			const rootnode: ProcedureFolderNode = {
				GroupNumber: 0,
				Level: 0,
				Name: "[Root]",
				Parent: 0,
			};
			response.data?.Folders.unshift(rootnode);
		}
	}

	return response;
};

export const useFetchProcedureGroupDetails = (id: number | undefined) => {
	const user = useContext(UserContext);
	return useQuery({
		queryKey: [ProcedureQueryKeys.groupdetails, id, user?.CompanyId],
		queryFn: () => SATOSPortal.GetProcedureGroupDetails(id),
		enabled: !!id,
	});
};

export const useFetchProcedureShiftConfiguration = () => {
	const user = useContext(UserContext);
	return useQuery({
		queryKey: [
			ProcedureQueryKeys.procedure,
			ProcedureQueryKeys.shift,
			user?.CompanyId,
		],
		queryFn: () => SATOSPortal.GetProcedureShiftConfiguration(),
		enabled: !!user?.CompanyId,
	});
};
